import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Button, Card, Chip, Input, Skeleton, Tabs } from "../ui";
import Wizard, { Counted } from "./Wizard";
import ImageUploader from "./ImageUploader";
import TagFilter from "../character/TagFilter";
import { useAsync } from "../../hooks";
import { useToast } from "../../context/ToastContext";
import content from "../../services/api-content";
import "../../styles/content.css";

const STEPS = [
  { key: "basic", title: "ข้อมูลพื้นฐาน" },
  { key: "chars", title: "ตัวละครในจักรวาล" },
  { key: "greet", title: "คำทักทายและรูปปก" },
  { key: "privacy", title: "แท็กและความเป็นส่วนตัว" },
];
const MAX_CHARS = 20;

function CharPicker({ selected, onChange }) {
  const [tab, setTab] = useState("mine");
  const [q, setQ] = useState("");
  const mine = useAsync(() => content.characters.mine(), []);
  const found = useAsync(() => (tab === "search" ? content.characters.list({ q, limit: 12, sort: "popular" }).then((r) => r.items) : Promise.resolve([])), [tab, q]);
  const rows = tab === "mine" ? (mine.data || []).filter((c) => c.status === "approved" && !c.isArchived) : found.data || [];
  const has = (id) => selected.some((c) => c.id === id);
  const toggle = (c) => onChange(has(c.id) ? selected.filter((x) => x.id !== c.id) : selected.length >= MAX_CHARS ? selected : [...selected, { id: c.id, name: c.name, image: c.image, emoji: c.emoji, colors: c.colors }]);
  return (
    <div>
      <div className="strong mb-8">เลือกแล้ว {selected.length}/{MAX_CHARS}</div>
      <div className="chip-row mb-16" style={{ flexWrap: "wrap" }}>
        {selected.length ? selected.map((c) => <Chip key={c.id} active onClick={() => toggle(c)}>{c.name} ✕</Chip>) : <span className="faint">ยังไม่ได้เลือกตัวละคร</span>}
      </div>
      <Tabs tabs={[{ value: "mine", label: "ตัวละครของฉัน" }, { value: "search", label: "ค้นหาตัวละครอื่น" }]} value={tab} onChange={setTab} />
      {tab === "search" ? <div className="mt-16"><Input type="search" aria-label="ค้นหาตัวละคร" placeholder="ค้นหาชื่อตัวละคร..." value={q} onChange={(e) => setQ(e.target.value)} /></div> : null}
      <div className="stack mt-16">
        {(tab === "mine" ? mine.loading : found.loading) ? <Skeleton height={56} /> : rows.length ? rows.map((c) => (
          <label key={c.id} className="check card" style={{ padding: 10 }}>
            <input type="checkbox" checked={has(c.id)} onChange={() => toggle(c)} />
            <Avatar name={c.name} seed={c.id} colors={c.colors} src={c.image} size={36} square character />
            <span className="grow"><b className="clamp-1" style={{ display: "block" }}>{c.name}</b><span className="faint clamp-1" style={{ fontSize: 12, display: "block" }}>{c.tagline}</span></span>
          </label>
        )) : <p className="muted">{tab === "mine" ? "ยังไม่มีตัวละครที่เผยแพร่ — สร้างตัวละครก่อน หรือค้นหาตัวละครของคนอื่น" : "ไม่พบตัวละคร"}</p>}
      </div>
    </div>
  );
}

export default function UniverseForm({ editId }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [f, setF] = useState({ id: null, name: "", tagline: "", history: "", description: "", memoryMode: "standard20", chars: [], greeting: "", images: [], tags: [], visibility: "public" });
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const allTags = useAsync(() => content.tags.list(), []);
  const loaded = useAsync(() => (editId ? content.universes.get(editId) : Promise.resolve(null)), [editId]);

  useEffect(() => {
    const u = loaded.data;
    if (u) setF({ id: u.id, name: u.name || "", tagline: u.tagline || "", history: u.history || "", description: u.description || "", memoryMode: u.memoryMode || "standard20", chars: (u.characters || []).map((c) => ({ id: c.id, name: c.name, image: c.image, emoji: c.emoji, colors: c.colors })), greeting: u.greeting || "", images: u.image ? [u.image] : [], tags: u.tags || [], visibility: u.visibility || "public" });
  }, [loaded.data]);

  if (editId && loaded.loading) return <Skeleton height={300} radius={16} />;

  const save = async () => {
    if (!f.name.trim()) { setStep(0); return toast("กรุณากรอกชื่อจักรวาล", "error"); }
    if (!f.chars.length) { setStep(1); return toast("เลือกตัวละครอย่างน้อย 1 ตัว", "error"); }
    setBusy(true);
    try {
      const u = await content.universes.save({ ...(f.id ? { id: f.id } : {}), name: f.name.trim(), tagline: f.tagline, history: f.history, description: f.description, memoryMode: f.memoryMode, characterIds: f.chars.map((c) => c.id), greeting: f.greeting, image: f.images[0] || "", tags: f.tags, visibility: f.visibility });
      toast("บันทึกจักรวาลแล้ว", "success");
      navigate(`/universe/${u.id}`);
    } catch (e) { toast(e.message, "error"); } finally { setBusy(false); }
  };

  const body = [
    <>
      <Input label="ชื่อจักรวาล *" maxLength={40} value={f.name} onChange={(e) => set({ name: e.target.value })} /><Counted value={f.name} max={40} />
      <Input label="คำโปรย" maxLength={100} value={f.tagline} onChange={(e) => set({ tagline: e.target.value })} /><Counted value={f.tagline} max={100} />
      <Input multiline label="ประวัติจักรวาล (ผู้เล่นอ่านได้)" maxLength={8000} value={f.history} onChange={(e) => set({ history: e.target.value })} /><Counted value={f.history} max={8000} />
      <Input multiline label="คำอธิบายจักรวาล (ให้ AI — ผู้เล่นไม่เห็น)" maxLength={10000} value={f.description} onChange={(e) => set({ description: e.target.value })} /><Counted value={f.description} max={10000} />
      <div className="strong mb-8">โหมดความจำ</div>
      <div className="pill-opt" role="radiogroup" aria-label="โหมดความจำ">{[["standard20", "มาตรฐาน — จำ 20 ข้อความ"], ["extended40", "ขยาย — จำ 40 ข้อความ"]].map(([v, l]) => <Chip key={v} active={f.memoryMode === v} onClick={() => set({ memoryMode: v })}>{l}</Chip>)}</div>
    </>,
    <CharPicker selected={f.chars} onChange={(chars) => set({ chars })} />,
    <>
      <Input multiline label="คำทักทายของจักรวาล" maxLength={3000} value={f.greeting} onChange={(e) => set({ greeting: e.target.value })} /><Counted value={f.greeting} max={3000} />
      <div className="strong mb-8">รูปปก</div>
      <ImageUploader value={f.images} onChange={(images) => set({ images })} max={1} type="character" label="เพิ่มรูปปก" />
    </>,
    <>
      <div className="row-between mb-8"><div className="strong">แท็ก ({f.tags.length}/12)</div><Button size="sm" onClick={() => setTagOpen(true)}>เลือกแท็ก</Button></div>
      <div className="chip-row mb-16" style={{ flexWrap: "wrap" }}>{f.tags.length ? f.tags.map((t) => <Chip key={t} active onClick={() => set({ tags: f.tags.filter((x) => x !== t) })}>{t} ✕</Chip>) : <span className="faint">ยังไม่ได้เลือกแท็ก</span>}</div>
      <div className="strong mb-8">การมองเห็น</div>
      <div className="stack" role="radiogroup" aria-label="การมองเห็น">
        {[["public", "สาธารณะ"], ["unlisted", "ไม่แสดงในรายการ"], ["private", "ส่วนตัว"]].map(([v, l]) => <label key={v} className="check"><input type="radio" name="uvis" checked={f.visibility === v} onChange={() => set({ visibility: v })} /> {l}</label>)}
      </div>
      <Card className="mt-16" style={{ fontSize: 13 }}><span className="muted">จักรวาลต้องไม่มีเนื้อหา 18+ — ระบบจะตรวจและปฏิเสธเนื้อหาที่ไม่เหมาะสมอัตโนมัติ</span></Card>
      <TagFilter open={tagOpen} onClose={() => setTagOpen(false)} tags={allTags.data || []} selected={f.tags} onChange={(tags) => set({ tags })} max={12} title="แท็กจักรวาล" />
    </>,
  ];
  return (
    <Wizard steps={STEPS} step={step} setStep={setStep} title={editId ? "แก้ไขจักรวาล" : "สร้างจักรวาล"}
      actions={step === STEPS.length - 1 ? <Button variant="primary" onClick={save} disabled={busy}>{busy ? "กำลังบันทึก..." : "บันทึกจักรวาล"}</Button> : <Button onClick={save} disabled={busy}>บันทึก</Button>}>
      {body[step]}
    </Wizard>
  );
}
